import React, { useState, useEffect } from 'react';
import {
  Form,
  Input,
  Select,
  Button,
  Card,
  Row,
  Col,
  message,
  Spin,
  Space,
  Divider,
  Tag,
} from 'antd';
import { ArrowLeftOutlined, SaveOutlined } from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { StringOption } from '../../types';
import {
  OperatorGroup,
  OperatorGroupFormData,
  CreateOperatorGroupCommand,
  UpdateOperatorGroupCommand,
  Division,
  Worker,
} from '../../types/operatorGroup';
import operatorGroupService from '../../services/operatorGroupService';
import categoryService from '../../services/categoryService';
import { authService } from '../../services/authService';
import { useErrorHandling } from '../../hooks/useErrorHandling';

const { Option } = Select;

// 定义负责团队选项
const RESPONSIBLE_TEAM_OPTIONS: { value: any; label: string }[] = [
  { value: 'Planogram Team', label: 'Planogram Team' },
  { value: 'Construction Team', label: 'Construction Team' },
  { value: 'Commercial Team', label: 'Commercial Team' },
  { value: 'Marketing Team', label: 'Marketing Team' },
  { value: 'Operation Team', label: 'Operation Team' },
  { value: 'IT Team', label: 'IT Team' },
];

interface OperatorGroupFormPageProps {
  mode: 'create' | 'edit';
}

const OperatorGroupFormPage: React.FC<OperatorGroupFormPageProps> = ({ mode }) => {
  const navigate = useNavigate();
  const { code } = useParams<{ code?: string }>();
  const { t } = useTranslation();
  const { handleAsyncOperation, showError, showSuccess } = useErrorHandling();
  
  // 状态管理
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(mode === 'edit');
  const [operatorGroup, setOperatorGroup] = useState<OperatorGroup | null>(null);
  const [workers, setWorkers] = useState<Worker[]>([]);
  const [directors, setDirectors] = useState<Worker[]>([]);
  
  const [form] = Form.useForm();

  // 初始化
  useEffect(() => {
    loadInitialData();
  }, []);

  // 加载初始数据
  const loadInitialData = async () => {
    try {
      // 并行加载基础数据
      const [accountsResponse, operatorGroupResponse] = await Promise.all([
        authService.listAccount(),
        mode === 'edit' && code ? operatorGroupService.getOperatorGroupByCode(code) : Promise.resolve(null),
      ]);

      // 处理账户数据（操作员和负责人都来自同一数据源）
      if (accountsResponse) {
        // 获取数据，兼容mock和真实API响应格式
        const response = accountsResponse as any;
        const accountData = response.success ? response.data : response.data;
        if (accountData && Array.isArray(accountData)) {
          // 将选项格式转换为Worker类型
          const accountList: Worker[] = accountData.map((option: { value: string; label: string }) => ({
            accountId: option.value,
            name: option.label
          }));
          setWorkers(accountList);
          setDirectors(accountList); // 负责人选项与操作员选项相同
        }
      }

      // 处理操作组数据（编辑模式）
      if (mode === 'edit' && operatorGroupResponse?.success && operatorGroupResponse.data) {
        const data = operatorGroupResponse.data;
        setOperatorGroup(data);
        
        // 设置表单初始值
        form.setFieldsValue({
          code: data.code,
          name: data.name,
          responsibleTeam: data.responsibleTeam, // 使用responsibleTeam字段
          director: data.director,
          workers: data.workers.map(w => w.accountId),
          status: data.status,
        });
      }
    } catch (error) {
      console.error('加载初始数据失败:', error);
      message.error(t('operatorGroup.messages.loadFailed'));
    } finally {
      setInitialLoading(false);
    }
  };

  // 操作员选择变化处理（现在只是占位符，因为负责人选择独立于操作员选择）
  const handleWorkersChange = (selectedWorkerIds: string[]) => {
    // 负责人选择现在独立于操作员选择，不需要特殊处理
  };

  // 表单提交
  const handleSubmit = async (values: OperatorGroupFormData) => {
    setLoading(true);
    try {
      // 构建操作员数组
      const selectedWorkers = workers.filter(worker => 
        values.workers.includes(worker.accountId)
      );

      if (mode === 'create') {
        // 创建操作组
        const command: CreateOperatorGroupCommand = {
          code: values.code,
          name: values.name,
          responsibleTeam: values.responsibleTeam, // 使用responsibleTeam字段
          director: values.director,
          workers: selectedWorkers,
          status: values.status,
        };

        const response = await operatorGroupService.createOperatorGroup(command);
        if (response.success) {
          showSuccess(t('operatorGroup.messages.createSuccess'));
          navigate('/operator-group');
        } else {
          showError(t('operatorGroup.messages.createFailed'));
        }
      } else {
        // 更新操作组
        const command: UpdateOperatorGroupCommand = {
          code: values.code,
          name: values.name,
          director: values.director,
          workers: selectedWorkers,
          status: values.status,
        };

        const response = await operatorGroupService.updateOperatorGroup(command);
        if (response.success) {
          showSuccess(t('operatorGroup.messages.updateSuccess'));
          navigate('/operator-group');
        } else {
          showError(t('operatorGroup.messages.updateFailed'));
        }
      }
    } catch (error) {
      console.error('操作失败:', error);
      message.error(t('operatorGroup.messages.operationFailed'));
    } finally {
      setLoading(false);
    }
  };

  // 取消操作
  const handleCancel = () => {
    navigate('/operator-group');
  };

  // 检查编码唯一性
  const validateCode = async (rule: any, value: string) => {
    if (!value) return;
    if (mode === 'edit' && value === operatorGroup?.code) return;
  };

  // 表单验证规则
  const formRules = {
    code: [
      { required: true, message: t('operatorGroup.validation.codeRequired') },
      { validator: validateCode },
    ],
    name: [
      { required: true, message: t('operatorGroup.validation.nameRequired') },
      { max: 50, message: t('operatorGroup.validation.nameMaxLength') },
    ],
    responsibleTeam: [ // 修改为responsibleTeam验证规则
      { required: true, message: t('operatorGroup.validation.divisionRequired') },
    ],
    workers: [
      { required: true, message: t('operatorGroup.validation.workersRequired') },
    ],
    director: [
      { required: true, message: t('operatorGroup.validation.directorRequired') },
    ],
  };

  if (initialLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div>
      <Card>
        {/* 页面头部 */}
        <div className="page-header">
          <Row justify="space-between" align="middle">
            <Col>
              <Space>
                <Button
                  type="text"
                  icon={<ArrowLeftOutlined />}
                  onClick={handleCancel}
                >
                  {t('operatorGroup.back')}
                </Button>
                <Divider type="vertical" />
                <h2 style={{ margin: 0 }}>
                  {mode === 'create' ? t('operatorGroup.createTitle') : t('operatorGroup.editTitle')}
                </h2>
              </Space>
            </Col>
          </Row>
        </div>

        <Divider />

        {/* 表单区域 */}
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={{
            workers: [],
            status: 'ENABLE',
          }}
        >
          <Row gutter={[24, 16]}>
            {mode === 'edit' && (<Col xs={24} sm={12} md={8}>
              <Form.Item
                name="code"
                label={t('operatorGroup.form.codeLabel')}
                rules={formRules.code}
              >
                <Input
                  placeholder={t('operatorGroup.form.codePlaceholder')}
                  disabled={mode === 'edit'}
                  maxLength={20}
                />
              </Form.Item>
            </Col>)
            }
            
            <Col xs={24} sm={12} md={8}>
              <Form.Item
                name="name"
                label={t('operatorGroup.form.nameLabel')}
                rules={formRules.name}
              >
                <Input
                  placeholder={t('operatorGroup.form.namePlaceholder')}
                  maxLength={50}
                />
              </Form.Item>
            </Col>

            {/* 将divisionCode替换为responsibleTeam */}
            <Col xs={24} sm={12} md={8}>
              <Form.Item
                name="responsibleTeam"
                label={t('operatorGroup.form.divisionLabel')} // 保持使用原有的国际化键
                rules={formRules.responsibleTeam}
              >
                <Select
                  placeholder={t('operatorGroup.form.divisionPlaceholder')} // 保持使用原有的国际化键
                  disabled={mode === 'edit'}
                  showSearch
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                    option?.children?.toString().toLowerCase().includes(input.toLowerCase())
                  }
                >
                  {RESPONSIBLE_TEAM_OPTIONS.map(team => (
                    <Option key={team.value} value={team.value}>
                      {team.label}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>

            <Col xs={24} sm={12} md={8}>
              <Form.Item
                name="status"
                label={t('operatorGroup.form.statusLabel')}
                initialValue="ENABLE"
              >
                <Select placeholder={t('operatorGroup.form.statusPlaceholder')}>
                  <Option value="ENABLE">
                    <Tag color="green">{t('operatorGroup.status.ENABLE')}</Tag>
                  </Option>
                  <Option value="DISABLE">
                    <Tag color="red">{t('operatorGroup.status.DISABLE')}</Tag>
                  </Option>
                </Select>
              </Form.Item>
            </Col>

            <Col xs={24} sm={12} md={8}>
              <Form.Item
                name="workers"
                label={t('operatorGroup.form.workersLabel')}
                rules={formRules.workers}
              >
                <Select
                  mode="multiple"
                  placeholder={t('operatorGroup.form.workersPlaceholder')}
                  showSearch
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                    option?.children?.toString().toLowerCase().includes(input.toLowerCase())
                  }
                  onChange={handleWorkersChange}
                  maxTagCount="responsive"
                >
                  {workers.map(worker => (
                    <Option key={worker.accountId} value={worker.accountId}>
                      {worker.name} ({worker.accountId})
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>

            <Col xs={24} sm={12} md={8}>
              <Form.Item
                name="director"
                label={t('operatorGroup.form.directorLabel')}
                rules={formRules.director}
              >
                <Select
                  placeholder={t('operatorGroup.form.directorPlaceholder')}
                  showSearch
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                    option?.children?.toString().toLowerCase().includes(input.toLowerCase())
                  }
                >
                  {directors.map(director => (
                    <Option key={director.accountId} value={director.accountId}>
                      {director.name} ({director.accountId})
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          {/* 选中的操作员预览 */}
          <Form.Item label={t('operatorGroup.detail.workersInfo')}>
            <Form.Item noStyle shouldUpdate={(prevValues, currentValues) => 
              prevValues.workers !== currentValues.workers
            }>
              {({ getFieldValue }) => {
                const selectedWorkerIds = getFieldValue('workers') || [];
                const selectedWorkers = workers.filter(worker => 
                  selectedWorkerIds.includes(worker.accountId)
                );
                
                return (
                  <div style={{ minHeight: '32px' }}>
                    {selectedWorkers.length > 0 ? (
                      <Space wrap>
                        {selectedWorkers.map(worker => (
                          <Tag key={worker.accountId} color="blue">
                            {worker.name} ({worker.accountId})
                          </Tag>
                        ))}
                      </Space>
                    ) : (
                      <span style={{ color: '#999' }}>{t('operatorGroup.detail.noWorkers')}</span>
                    )}
                  </div>
                );
              }}
            </Form.Item>
          </Form.Item>

          {/* 表单操作按钮 */}
          <Divider />
          <Form.Item>
            <Space>
              <Button
                type="primary"
                htmlType="submit"
                icon={<SaveOutlined />}
                loading={loading}
              >
                {mode === 'create' ? t('operatorGroup.create') : t('operatorGroup.save')}
              </Button>
              <Button onClick={handleCancel}>
                {t('operatorGroup.cancel')}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Card>
    </div>
  );
};

export default OperatorGroupFormPage;